import {
  DatasetMetadata,
  MULTIPART_CHUNK_SIZE,
  MULTIPART_THRESHOLD_BYTES,
  UploadFile,
  abortMultipartUpload,
  completeMultipartUpdate,
  fileToUploadFile,
  initiateMultipartUpdateUpload,
  updateDataset,
  uploadPartToS3,
} from '@app/services/dataUploadService'
import { useState } from 'react'

export const useDatasetUpdate = () => {
  const [updating, setUpdating] = useState(false)
  const [progress, setProgress] = useState(0)

  const runMultipartUpdate = async (
    id: string,
    primary: File,
    companions: UploadFile[],
    uploadId: string,
    key: string,
    presignedUrls: string[],
    metadata?: Partial<DatasetMetadata>,
    config?: Record<string, unknown>,
  ): Promise<void> => {
    let completed = 0
    try {
      const parts = await Promise.all(
        presignedUrls.map(async (url, i) => {
          const chunk = primary.slice(i * MULTIPART_CHUNK_SIZE, (i + 1) * MULTIPART_CHUNK_SIZE)
          const etag = await uploadPartToS3(url, chunk)
          setProgress(Math.round((++completed / presignedUrls.length) * 90))
          return { PartNumber: i + 1, ETag: etag }
        }),
      )
      await completeMultipartUpdate(id, key, uploadId, parts, metadata, companions, config)
    } catch (err) {
      await abortMultipartUpload(key, uploadId)
      throw err
    }
  }

  const update = async (
    id: string,
    files: File[],
    metadata?: Partial<DatasetMetadata>,
    config?: Record<string, unknown>,
  ): Promise<void> => {
    const [primary, ...companionFiles] = files
    const isMultipart = primary && primary.size >= MULTIPART_THRESHOLD_BYTES

    setUpdating(true)
    setProgress(0)

    try {
      if (isMultipart) {
        const total_parts = Math.ceil(primary.size / MULTIPART_CHUNK_SIZE)
        const { uploadId, key, presignedUrls } = await initiateMultipartUpdateUpload(
          id,
          primary,
          metadata?.name ?? '',
          total_parts,
        )
        if (!presignedUrls?.length) throw new Error('Initiate response missing presigned URLs')
        const companions = await Promise.all(companionFiles.map(fileToUploadFile))
        await runMultipartUpdate(
          id,
          primary,
          companions,
          uploadId,
          key,
          presignedUrls,
          metadata,
          config,
        )
      } else {
        const uploadFiles = files.length
          ? await Promise.all(files.map(fileToUploadFile))
          : undefined
        await updateDataset(id, { metadata, config, files: uploadFiles })
      }
      setProgress(100)
    } finally {
      setUpdating(false)
    }
  }

  return { update, updating, progress }
}
